/**
 * Provider Profile Section Component
 *
 * Displays provider profile information including avatar, name, credentials,
 * contact information, and action buttons for editing profile.
 *
 * @module components/provider-dashboard/ProviderProfileSection
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useTheme } from '@/hooks/useTheme';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';
import { apiService, getLogger } from '@/services';
import type { ProfileGetResponse } from '@/types/provider';
import { PROVIDER_ROLE_OPTIONS } from '@/constants/provider';

/**
 * Get the display label for a provider role
 */
const getRoleLabel = (roleValue: string): string => {
  const roleOption = PROVIDER_ROLE_OPTIONS.find(option => option.value === roleValue);
  return roleOption?.label || roleValue;
};

/**
 * Provider Profile Section Component
 */
export const ProviderProfileSection: React.FC = () => {
  const logger = getLogger();
  const navigate = useNavigate();
  const { profile, isLoading } = useUserProfile();
  const { isDark } = useTheme();

  // State for profile image URL from ApiService
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [providerProfile, setProviderProfile] = useState<ProfileGetResponse | null>(null);
  const [providerLoading, setProviderLoading] = useState(false);

  // Fetch provider profile and photo
  useEffect(() => {
    const fetchProviderData = async () => {
      setProviderLoading(true);
      setPhotoLoading(true);

      try {
        const response = await apiService.provider.getProfile();
        setProviderProfile(response);

        if (response?.photo) {
          setProfileImageUrl(response.photo);
        }
      } catch (error) {
        logger.error('Failed to fetch provider profile:', error);
      } finally {
        setProviderLoading(false);
        setPhotoLoading(false);
      }
    };

    fetchProviderData();
  }, []);

  const handleEditProfile = () => {
    navigate('/provider/edit-profile');
  };

  if (isLoading || providerLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="w-24 h-24 bg-neutral-300 rounded-full mx-auto"></div>
          <div className="h-4 bg-neutral-300 rounded w-3/4 mx-auto"></div>
          <div className="h-10 bg-neutral-300 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex flex-col items-center space-y-6">
        {/* Avatar Photo */}
        <AvatarPhoto
          src={profileImageUrl}
          firstName={profile?.firstName}
          lastName={profile?.lastName}
          size="xl"
          loading={photoLoading}
          onError={() => setProfileImageUrl(null)}
        />

        {/* Provider Name */}
        <div className="text-center">
          <TitleText level={3} className="mb-1">
            {profile?.firstName && profile?.lastName
              ? `${profile.firstName} ${profile.lastName}`
              : 'Provider Profile'
            }
          </TitleText>
          {providerProfile?.ahpra_number && (
            <BodyText variant="muted" size="sm">
              AHPRA: {providerProfile.ahpra_number}
            </BodyText>
          )}
          {providerProfile?.provider_role && (
            <BodyText variant="muted" size="sm">
              {getRoleLabel(providerProfile.provider_role)}
            </BodyText>
          )}
        </div>

        {/* Provider Profile Information */}
        {providerProfile && (
          <div className="w-full space-y-3">
            {providerProfile.practice_name && (
              <div className={`p-3 rounded-lg ${
                isDark ? 'bg-secondary-800' : 'bg-neutral-200'
              }`}>
                <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                  Practice:
                </BodyText>
                <BodyText size="sm">
                  {providerProfile.practice_name}
                </BodyText>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full space-y-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleEditProfile}
            className="w-full"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Profile
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ProviderProfileSection;